import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteEmployee,
  getBaseSalaryRange,
  getPaginatedEmployees,
  removeEmployee,
} from 'src/redux/slices/employees';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  EMPLOYEE_EPF_STATUS,
  IEmployeeBaseSalaryRange,
  IEmployeeListItem,
  IEmployeeTableFilters,
  IEmployeeTableFilterValue,
  IEmployeeTableSort,
} from 'src/types/employee';

import EmployeeTableRow from '../employee-table-row';
import EmployeeTableToolbar from '../employee-table-toolbar';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', align: 'left', primary: true },
  { id: 'baseSalary', label: 'Base Salary', align: 'left', primary: true },
  { id: 'epf', label: 'EPF', align: 'left', primary: true },
  { id: 'updatedAt', label: 'Updated', align: 'left', primary: true },
  { id: '', primary: true },
];

const defaultFilters: IEmployeeTableFilters = {
  generalSearch: '',
  epf: EMPLOYEE_EPF_STATUS.ALL,
  baseSalary: null,
};

const defaultSortBy: IEmployeeTableSort = {
  sortBy: 'employeeNumber',
  order: 'asc',
};

export default function EmployeeListView() {
  const { status, employees, error, baseSalary } = useAppSelector((state) => state.employees);

  const salaryRange: IEmployeeBaseSalaryRange[] = baseSalary.data;

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const handleFilters = useCallback(
    (name: string, value: IEmployeeTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.employees.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedEmployees = useCallback(() => {
    dispatch(
      getPaginatedEmployees({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        sort: sort.order,
        sortBy: sort.sortBy,
        epf:
          filters.epf === EMPLOYEE_EPF_STATUS.ALL
            ? null
            : filters.epf === EMPLOYEE_EPF_STATUS.ENABLED,
        baseSalary: filters.baseSalary,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (employee: IEmployeeListItem, index: number) => {
      dispatch(removeEmployee({ employeeId: employee._id }));

      try {
        await dispatch(deleteEmployee({ employee, index })).unwrap();

        fetchPaginatedEmployees();
        enqueueSnackbar('Employee deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedEmployees]
  );

  const fetchSalaryRange = useCallback(() => {
    dispatch(getBaseSalaryRange());
  }, [dispatch]);

  useEffect(() => {
    fetchPaginatedEmployees();
    fetchSalaryRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedEmployees, fetchSalaryRange]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Employees"
        links={[]}
        action={
          <>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_EMPLOYEE,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.employees.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Employee
              </Button>
            )}
          </>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <EmployeeTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
          salaryRange={salaryRange}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD.filter(({ primary }) => {
                  if (settings.themeLayout === 'vertical') {
                    return primary;
                  }
                  return true;
                })}
                rowCount={employees.employees.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {employees?.employees.map((row: IEmployeeListItem, index: number) => (
                      <EmployeeTableRow
                        key={row._id}
                        row={row}
                        selected={table.selected.includes(row._id)}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && employees.count === 0 && (
                  <TableNoData message="No employees found" notFound={employees.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={employees.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={(value) => {
            table.onChangeDense(value);
          }}
        />
      </Card>
    </Container>
  );
}
